import { apiClient } from './api-client';
import type {
  TicketType,
  TicketTypeFormSchema,
  ValidationResult,
  PaginatedResponse,
} from '@centraoneplus/shared-types';

export interface FieldGroupDto {
  fieldGroupId?: string;
  id?: string;
  key?: string;
  name?: string;
  description?: string;
  icon?: string;
  displayLocation?: string;
  isCollapsible?: boolean;
  isCollapsedByDefault?: boolean;
  sortOrder?: number;
  isRequired?: boolean;
  fields?: Array<{
    fieldDefinitionId: string;
    isRequired?: boolean;
    isVisible?: boolean;
    isVisibleInList?: boolean;
    sortOrder?: number;
  }>;
}

// Classification config types for AI classification
export interface FewShotExample {
  input: string;
  expectedType: boolean;
  explanation?: string;
}

export interface StructuralPattern {
  type: 'table' | 'list' | 'key_value';
  minColumns?: number;
  minItems?: number;
  requiredHeaders?: string[];
  itemPattern?: string;
  boostScore: number;
}

export interface ClassificationConfig {
  fewShotExamples?: FewShotExample[];
  confidenceThreshold?: number;
  structuralPatterns?: StructuralPattern[];
  intentKeywords?: string[];
  excludeKeywords?: string[];
  commercialIndicators?: string[];
  promptInstructions?: string;
  isCommercialType?: boolean;
}

export interface CreateTicketTypeDto {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  color?: string;
  isActive?: boolean;
  isDefault?: boolean;
  defaultPipelineId?: string;
  defaultSlaPolicyId?: string;
  ticketPrefix?: string;
  settings?: Record<string, any>;
  allowedRoles?: string[];
  allowedTeams?: string[];
  fallbackKeywords?: string[];
  classificationConfig?: ClassificationConfig;
  isCatchAll?: boolean;
  sortOrder?: number;
  fieldGroups?: FieldGroupDto[];
}

export interface UpdateTicketTypeDto extends Partial<CreateTicketTypeDto> {}

export interface TicketTypeFilters {
  active?: boolean;
  includeFields?: boolean;
}

class TicketTypesService {
  private readonly basePath = '/ticket-types';

  async getTicketTypes(filters?: TicketTypeFilters): Promise<TicketType[]> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.active !== undefined) {
        params.append('active', String(filters.active));
      }
      if (filters.includeFields !== undefined) {
        params.append('includeFields', String(filters.includeFields));
      }
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    const response = await apiClient.get<TicketType[]>(url);
    return response.data;
  }

  async getTicketType(id: string): Promise<TicketType> {
    const response = await apiClient.get<TicketType>(`${this.basePath}/${id}`);
    return response.data;
  }

  async getTicketTypeBySlug(slug: string): Promise<TicketType> {
    const response = await apiClient.get<TicketType>(`${this.basePath}/by-slug/${slug}`);
    return response.data;
  }

  async getDefaultTicketType(): Promise<TicketType | null> {
    try {
      const response = await apiClient.get<TicketType>(`${this.basePath}/default`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async createTicketType(dto: CreateTicketTypeDto): Promise<TicketType> {
    const response = await apiClient.post<TicketType>(this.basePath, dto);
    return response.data;
  }

  async updateTicketType(id: string, dto: UpdateTicketTypeDto): Promise<TicketType> {
    const response = await apiClient.put<TicketType>(`${this.basePath}/${id}`, dto);
    return response.data;
  }

  async deleteTicketType(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  async duplicateTicketType(id: string, name: string, slug?: string): Promise<TicketType> {
    const response = await apiClient.post<TicketType>(`${this.basePath}/${id}/duplicate`, {
      name,
      slug,
    });
    return response.data;
  }

  async getFormSchema(id: string, stageId?: string): Promise<TicketTypeFormSchema> {
    const params = new URLSearchParams();
    if (stageId) {
      params.append('stageId', stageId);
    }
    const queryString = params.toString();
    const url = queryString
      ? `${this.basePath}/${id}/form-schema?${queryString}`
      : `${this.basePath}/${id}/form-schema`;
    const response = await apiClient.get<TicketTypeFormSchema>(url);
    return response.data;
  }

  async validateFields(
    ticketTypeId: string,
    values: Record<string, any>,
    stageId?: string
  ): Promise<ValidationResult> {
    // Backend route is POST /validation/ticket (ticket-type based validation).
    const response = await apiClient.post<ValidationResult>('/validation/ticket', {
      ticketTypeId,
      values,
      stageId,
    });
    return response.data;
  }
}

export const ticketTypesService = new TicketTypesService();
